import type {
  ApplyResult,
  Change,
  Command,
  Interval,
  ProjectTree,
  SavedFilter,
  Series,
  SeriesId,
  Sort,
  SyncReport,
  TaskId,
  TaskView,
  When,
} from "./generated";

export type SyncStatus =
  | { state: "idle" }
  | { state: "syncing" }
  | { state: "synced"; at: Date }
  | { state: "offline"; reason: string };

export type ApiEvent =
  | { kind: "change"; change: Change }
  | { kind: "sync-status"; status: SyncStatus };

export type Unsubscribe = () => void;

export type ApiErrorCode = "unauthorized" | "unreachable" | "unavailable" | "other";

export class ApiError extends Error {
  readonly code: ApiErrorCode;

  constructor(code: ApiErrorCode, message: string) {
    super(message);
    this.name = "ApiError";
    this.code = code;
  }
}

/** The one interface the UI depends on; implemented over Tauri commands and over td-wasm. */
export interface TaskApi {
  query(filter: string, sort: Sort): Promise<TaskView[]>;
  get(id: TaskId): Promise<TaskView | undefined>;
  projects(): Promise<ProjectTree>;
  savedFilters(): Promise<SavedFilter[]>;
  series(id: SeriesId): Promise<Series | undefined>;
  seriesInstances(id: SeriesId): Promise<TaskView[]>;
  parseDate(text: string): Promise<When>;
  /** The calendar day (`YYYY-MM-DD`) the text names in the account time zone. */
  parseCalendarDay(text: string): Promise<string>;
  parseInterval(text: string): Promise<Interval>;
  apply(command: Command): Promise<ApplyResult>;
  undo(): Promise<ApplyResult>;
  redo(): Promise<ApplyResult>;
  sync(): Promise<SyncReport>;
  syncStatus(): SyncStatus;
  subscribe(listener: (event: ApiEvent) => void): Unsubscribe;
}

export interface ServerSettings {
  serverUrl: string;
  username: string;
}

export interface Credentials extends ServerSettings {
  password: string;
}

/** Owns the connection lifecycle that precedes having a `TaskApi`. */
export interface Backend {
  readonly kind: "desktop" | "web";
  savedServer(): Promise<ServerSettings | undefined>;
  resume(): Promise<TaskApi | undefined>;
  signIn(credentials: Credentials): Promise<TaskApi>;
  signOut(): Promise<void>;
}
